import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { parseTextFromHtmlString } from 'utils';

const TITLE_MAX_CHARLENGTH = 71;
const DESCRIPTION_MAX_CHARLENGTH = 160;
const SUPPORTED_LANGUAGES = ['en-us', 'fr', 'ko'];
const DEFAULT_LANGUAGE = 'en-us';

export interface ListingSeoMetaProps {
  brandName?: string;
  canonicalUrl: string;
  description?: string | null;
  image?: string;
  jsonLd?: Record<string, unknown> | null;
  title?: string | null;
}

/** The canonical with `lang=<lang>` added, keeping a hash-router fragment (`#!/event/x`) intact. */
const toLanguageAlternate = (canonicalUrl: string, lang: string): string => {
  if (lang === DEFAULT_LANGUAGE) return canonicalUrl;
  try {
    const url = new URL(canonicalUrl);
    url.searchParams.set('lang', lang);
    return url.toString();
  } catch {
    return canonicalUrl;
  }
};

/** `Title | Brand`, trimmed so the whole thing fits a search result title. */
const deriveListingPageTitle = (title: string, brandName = ''): string => {
  const availableCharLength = TITLE_MAX_CHARLENGTH - brandName.length;
  if (availableCharLength <= 0) {
    return brandName.substring(0, TITLE_MAX_CHARLENGTH);
  }
  return brandName
    ? `${title.substring(0, availableCharLength - 1)} | ${brandName}`
    : title.substring(0, TITLE_MAX_CHARLENGTH);
};

/**
 * The SEO tags of a listing's detail page — experience, collection or event: title, description,
 * Open Graph / Twitter, robots, hreflang alternates, the canonical and (optionally) JSON-LD. Every URL
 * tag uses `canonicalUrl`, so the canonical, `og:url`, hreflang and the sitemap agree.
 *
 * The host page may ship its own canonical; it is removed before ours is rendered so the page never
 * declares two.
 */
const ListingSeoMeta = ({
  brandName,
  canonicalUrl,
  description,
  image,
  jsonLd,
  title,
}: ListingSeoMetaProps) => {
  const [isHostCanonicalRemoved, setIsHostCanonicalRemoved] = useState(false);

  useEffect(() => {
    document.querySelector("link[rel='canonical']")?.remove();
    setIsHostCanonicalRemoved(true);
  }, []);

  const pageTitle = title ? deriveListingPageTitle(title, brandName) : '';

  // Fall back to the title, then the brand name, so a listing with no description never emits an
  // empty description / OG / Twitter tag.
  const metaDescription = useMemo(
    () =>
      parseTextFromHtmlString(
        description || title || brandName || '',
      ).substring(0, DESCRIPTION_MAX_CHARLENGTH),
    [description, title, brandName],
  );

  return (
    <Helmet>
      {pageTitle && <title>{pageTitle}</title>}
      {pageTitle && <meta property="og:title" content={pageTitle} />}
      {pageTitle && <meta name="twitter:title" content={pageTitle} />}
      <meta name="description" content={metaDescription} />
      <meta property="og:description" content={metaDescription} />
      <meta name="twitter:description" content={metaDescription} />
      <meta property="og:url" content={canonicalUrl} />
      {image && <meta property="og:image" content={image} />}
      {image && <meta name="twitter:image" content={image} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="robots" content="index, follow" />
      {SUPPORTED_LANGUAGES.map((lang) => (
        <link
          key={lang}
          rel="alternate"
          hrefLang={lang}
          href={toLanguageAlternate(canonicalUrl, lang)}
        />
      ))}
      {isHostCanonicalRemoved && <link rel="canonical" href={canonicalUrl} />}
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
};

export default ListingSeoMeta;
